import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Calendar, TrendingUp, TrendingDown } from 'lucide-react';

const MonthlyHistory = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSnapshots();
  }, []);

  const loadSnapshots = async () => {
    try {
      const res = await axios.get(`${API}/monthly-snapshots`);
      setSnapshots(res.data);
    } catch (error) {
      toast.error('Failed to load monthly history');
    } finally {
      setLoading(false);
    }
  };

  const createSnapshot = async () => {
    try {
      await axios.post(`${API}/monthly-snapshots/create`);
      toast.success('Monthly snapshot created!');
      loadSnapshots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create snapshot');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900">Monthly History</h2>
          <button
            onClick={createSnapshot}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-4 h-4" />
            <span>Create Snapshot</span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {snapshots.map((snapshot) => (
            <div key={snapshot.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center space-x-3 mb-6">
                <Calendar className="w-6 h-6 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-900">{snapshot.month}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-900">Total Gain</span>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-green-900">{snapshot.total_gain_dt.toFixed(2)} DT</p>
                  <div className="mt-2 text-xs text-green-700">
                    <div>EUR: {snapshot.gain_breakdown.eur.toFixed(2)}</div>
                    <div>USD: {snapshot.gain_breakdown.usd.toFixed(2)}</div>
                    <div>DT: {snapshot.gain_breakdown.dt.toFixed(2)}</div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">Cash</span>
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{snapshot.cash_dt.toFixed(2)} DT</p>
                  <div className="mt-2 text-xs text-blue-700">
                    <div>EUR: {snapshot.cash_breakdown.eur.toFixed(2)}</div>
                    <div>USD: {snapshot.cash_breakdown.usd.toFixed(2)}</div>
                    <div>DT: {snapshot.cash_breakdown.dt.toFixed(2)}</div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-900">Online</span>
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-900">{snapshot.online_dt.toFixed(2)} DT</p>
                  <div className="mt-2 text-xs text-purple-700">
                    <div>EUR: {snapshot.online_breakdown.eur.toFixed(2)}</div>
                    <div>USD: {snapshot.online_breakdown.usd.toFixed(2)}</div>
                    <div>DT: {snapshot.online_breakdown.dt.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total Reservations</p>
                  <p className="text-xl font-bold text-gray-900">{snapshot.total_reservations}</p>
                </div>
                {Object.entries(snapshot.reservations_by_type).map(([type, count]) => (
                  <div key={type} className="text-center">
                    <p className="text-sm text-gray-500">{type}</p>
                    <p className="text-xl font-bold text-gray-900">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default MonthlyHistory;
